/**
 * @fileoverview CompanyResolver Service - Subdomain to Company Resolution
 * @module @puffin-core/subdomain-routing/services/companyResolver
 * @description Resolves company information from subdomains via API.
 * Brand-agnostic — uses VITE_API_URL env var for API endpoint.
 *
 * Features:
 * - Real API integration for subdomain resolution
 * - Response caching with configurable TTL (default 5 minutes)
 * - Graceful error handling
 * - Reserved subdomain detection
 * - Development resolver injection via setDevCompanyResolver
 */

import { isBareLocalHost } from '@puffin-core/cross-app-policy';
import { RESERVED_SUBDOMAINS } from '@puffin-core/reserved-subdomains';
import { WEB_PROTOCOLS, isWebAddress } from '@puffin-core/web-address';
import { createFetchSeam } from '@puffin-core/api-client/fetch-seam';
import { getEnvironmentVariable } from '../config/environment.js';
import { isIPv4Address, isDevSubdomain } from '../config/subdomainConfig.js';

/**
 * API base URL for company endpoints.
 *
 * XAL-001 / Phase 3.2 (2026-05-23) — fail-fast layered defense against
 * the apex-VITE_API_URL deployment-config footgun documented in the
 * Phase 0.2 inventory. When the env is misconfigured to an absolute
 * cross-origin URL (e.g. `VITE_API_URL=https://paydaypuffin.com` on
 * the `dtownretail.paydaypuffin.com` subdomain), the
 * `/companies/resolve` fetch goes cross-origin → CORS preflight fails
 * → SubdomainRouter renders "Subdomain not found" → `?cat=` token is
 * never consumed. That was the receiving-side compound defect in the
 * user's UAT 2026-05-23 bug.
 *
 * Defense layers (Gen-0, no shim):
 *   - Build time: `createWebAppViteConfig` bakes in `assertRelativeViteApiUrl`
 *   - Pre-deploy: `pnpm lint:do-specs` + `pnpm check:do-drift` CI gates
 *   - Render time (Phase 2a): SubdomainRouter reads `getApiBaseUrlConfigError()`
 *     and renders a branded ConfigErrorPage + telemetry — no module-load throw
 *
 * Localhost (`http://localhost:*`) is explicitly allowed because some
 * dev workflows do set an absolute localhost URL.
 *
 * EMPTY-STRING FOOTGUN (XAL-001 / Phase 2a fix, 2026-06-22): the per-app
 * Dockerfiles declare `ARG VITE_API_URL` + `ENV VITE_API_URL=$VITE_API_URL`.
 * When the build arg is absent (the canonical "intentionally NOT set" state),
 * the ENV is baked as the EMPTY STRING `""`, not `undefined`. Vite then inlines
 * `import.meta.env.VITE_API_URL = ""`, and `getEnvironmentVariable` returns `""`
 * (its default only applies to `undefined`). An empty base URL makes the
 * `/companies/resolve` fetch hit `"/companies/resolve"` — the SPA's own nginx,
 * not the API — so it gets HTML, fails to parse, and SubdomainRouter renders
 * "Organization Not Found". The `|| '/api/v1'` makes empty OR unset resolve to
 * the canonical relative base, so the "intentionally NOT set" deployment posture
 * actually works in production.
 *
 * @constant
 * @type {string}
 */
const RAW_API_BASE_URL = getEnvironmentVariable('VITE_API_URL', '/api/v1') || '/api/v1';

const ABSOLUTE_URL_REGEX = /^https?:\/\//i;
const LOCALHOST_DEV_ESCAPE_REGEX = /^http:\/\/localhost(?::|\/|$)/i;

/**
 * Typed error for a cross-origin (apex / subdomain) `VITE_API_URL`. Carries
 * the offending value + an XAL-001 code so the SubdomainRouter boundary can
 * recognize it and render a branded config-error page (instead of the original
 * uncaught module-load throw that white-screened every SPA).
 */
export class ViteApiUrlConfigError extends Error {
  /** @param {string} rawUrl The misconfigured VITE_API_URL value. */
  constructor(rawUrl) {
    super(
      `[XAL-001] VITE_API_URL is "${rawUrl}" — must be relative (e.g. "/api/v1") or unset. ` +
        'Cross-origin VITE_API_URL leaks tenant identifiers across origins and breaks CORS at the receiving app. ' +
        'See GUIDES/shared/standards/cross-app-launcher.md (XAL-001).',
    );
    this.name = 'ViteApiUrlConfigError';
    this.code = 'XAL-001';
    this.rawUrl = rawUrl;
  }
}

/**
 * Evaluate a raw VITE_API_URL value WITHOUT throwing. Returns a
 * `ViteApiUrlConfigError` for a cross-origin absolute URL, else `null`.
 * `http://localhost[:port][/path]` is an explicit dev escape.
 *
 * @param {string} rawUrl
 * @returns {ViteApiUrlConfigError|null}
 */
export function evaluateApiBaseUrl(rawUrl) {
  if (ABSOLUTE_URL_REGEX.test(rawUrl) && !LOCALHOST_DEV_ESCAPE_REGEX.test(rawUrl)) {
    return new ViteApiUrlConfigError(rawUrl);
  }
  return null;
}

/**
 * Fail-closed validator — throws `ViteApiUrlConfigError` on a cross-origin
 * absolute URL, else returns the value unchanged.
 *
 * @param {string} rawUrl
 * @returns {string}
 * @throws {ViteApiUrlConfigError}
 */
export function assertRelativeApiBaseUrl(rawUrl) {
  const error = evaluateApiBaseUrl(rawUrl);
  if (error) {
    throw error;
  }
  return rawUrl;
}

// XAL-001 / Phase 2a (2026-06-22): computed at module load but DOES NOT throw.
// The prior design threw here at import time → the whole SPA white-screened
// before React could mount (no error boundary can catch a module-load throw).
// SubdomainRouter now reads `getApiBaseUrlConfigError()` during render and
// renders a branded ConfigErrorPage + emits telemetry; resolveCompanyFromSubdomain
// fails closed (throws) rather than issuing the cross-origin fetch.
const API_BASE_URL_CONFIG_ERROR = evaluateApiBaseUrl(RAW_API_BASE_URL);

/**
 * The detected VITE_API_URL configuration error, or `null` when the value is
 * relative / unset / localhost. Read by SubdomainRouter at render time.
 *
 * @returns {ViteApiUrlConfigError|null}
 */
export function getApiBaseUrlConfigError() {
  return API_BASE_URL_CONFIG_ERROR;
}

const API_BASE_URL = RAW_API_BASE_URL;

/**
 * Cache Time-To-Live in milliseconds (5 minutes)
 * @constant
 * @type {number}
 */
const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * SUBDOMAIN-RESOLVE-001: bounded resolve-fetch timeout.
 *
 * A stalled connection (cold serverless spin-up, TCP-connect stall, DNS hang)
 * would otherwise leave the `/companies/resolve` fetch pending indefinitely —
 * the `useCompanyResolution` hook would sit in LOADING forever and the
 * retry/backoff machinery could NEVER engage (it only fires on a settled
 * promise). Aborting after this deadline converts the hang into a retryable
 * ERROR (handled by the fetch catch), so the exact cold-start failure this
 * initiative targets self-heals instead of spinning forever.
 *
 * @constant
 * @type {number}
 */
const RESOLVE_FETCH_TIMEOUT_MS = 8000;

/**
 * SUBDOMAIN-RESOLVE-001: discriminated resolve outcome.
 *
 * The resolver used to collapse EVERY failure (non-OK HTTP, malformed body,
 * thrown/aborted fetch) into the same `null` return as a genuinely-absent
 * tenant — so a one-time cold-start network hiccup rendered a permanent, scary
 * "Subdomain not found" page. `resolveCompanyFromSubdomain` now returns one of
 * these statuses so callers can distinguish "definitely not a tenant" (terminal,
 * no retry) from "the lookup failed" (transient, retry then show a distinct
 * "temporarily unavailable" state).
 *
 * @readonly
 * @enum {string}
 */
export const ResolveStatus = Object.freeze({
  /** A tenant was resolved — `companyId` (+ `companyData`) present. */
  RESOLVED: 'resolved',
  /** The backend definitively reported no tenant (404/403/400) — terminal. */
  NOT_FOUND: 'not-found',
  /** The lookup itself failed (5xx / network / offline / malformed) — retryable. */
  ERROR: 'error',
});

/** Structured-log prefix so these diagnostics are greppable in the console. */
const RESOLVE_LOG_PREFIX = '[SUBDOMAIN-RESOLVE-001]';

/**
 * Emit a structured resolver warning. SUBDOMAIN-RESOLVE-001 replaced the three
 * bare `catch {}` blocks that silently swallowed the real error; this surfaces
 * it (subdomain / url / httpStatus / error) without throwing. Kept as debug
 * output — do not remove without authorization.
 *
 * @param {string} message
 * @param {Object} context
 * @returns {void}
 */
function logResolverWarning(message, context) {
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(`${RESOLVE_LOG_PREFIX} ${message}`, context);
  }
}

/**
 * Read the capabilities off a resolved tenant, LOUDLY when the transport dropped them.
 *
 * 🔴 Owner ruling R-15 (2026-09-10): a capability-less tenant must not be mounted in
 * silence. This separates the two states a bare `capabilities || []` collapses:
 *
 * | state | meaning | verdict |
 * | --- | --- | --- |
 * | `capabilities` is an array | the resolve transported them; `[]` means the tenant genuinely has none | silent, correct |
 * | `capabilities` is absent | this resolve path did not send them — a TRANSPORT gap | loud |
 *
 * The second is the shape that shipped: `/resolve-domain` attached capabilities and
 * `/resolve` did not, so the same tenant produced two different objects and a
 * subdomain-hosted storefront lost every capability gate while looking perfectly healthy
 * from both ends.
 *
 * It **warns and degrades rather than throwing**, deliberately. This runs on a PUBLIC
 * storefront, where a throw is a white screen for a shopper — strictly worse than a
 * missing account link. Loud means visible to whoever can act on it, not fatal to whoever
 * cannot.
 *
 * @param {Object|null} companyData - A resolved tenant from `resolveCompanyFromSubdomain`.
 * @param {Object} [options]
 * @param {string} [options.consumer] - Who is asking, named in the warning.
 * @returns {{capabilities: string[], transportMissing: boolean}}
 */
export function assertResolvedCapabilities(companyData, { consumer } = {}) {
  const capabilities = companyData && companyData.capabilities;
  if (Array.isArray(capabilities)) {
    return { capabilities: [...capabilities], transportMissing: false };
  }
  logResolverWarning(
    'resolved tenant carries NO `capabilities` key — every capability gate will read false',
    {
      consumer: consumer || '(unnamed)',
      companyId: (companyData && companyData.companyId) || null,
      remedy:
        'configure `options.capabilityResolver` on this app’s createCompaniesSubdomainRouter; ' +
        'it feeds BOTH /resolve and /resolve-domain',
    },
  );
  return { capabilities: [], transportMissing: true };
}

/**
 * A non-OK resolve response is retryable ONLY for server-side / throttling
 * statuses (5xx, 408, 429). Every other 4xx — 404 `SUBDOMAIN_NOT_FOUND`, 403
 * `COMPANY_SUSPENDED`/`_CANCELLED`/reserved, 400 bad-format — is a DEFINITIVE
 * miss and must never be retried or shown as "temporarily unavailable".
 *
 * @param {number} status - HTTP status code.
 * @returns {boolean}
 */
export function isRetryableHttpStatus(status) {
  return status >= 500 || status === 408 || status === 429;
}

/**
 * Build a `RESOLVED` result.
 * @param {string} companyId
 * @param {Object} [companyData]
 * @returns {{status: string, companyId: string, companyData: (Object|undefined)}}
 */
function resolvedResult(companyId, companyData) {
  return { status: ResolveStatus.RESOLVED, companyId, companyData };
}

/**
 * Build a `NOT_FOUND` result (definitive absence — terminal, non-retryable).
 * @returns {{status: string}}
 */
function notFoundResult() {
  return { status: ResolveStatus.NOT_FOUND };
}

/**
 * The longest a server's `Retry-After` is honoured for (PALM-PHONE-UAT-001 C7). A 429's hint is the
 * limiter's whole window (60 s); waiting a minute on a boot screen is worse than one more refused
 * attempt, so the hint is capped and the hook's own backoff fills the rest.
 */
export const MAX_RETRY_AFTER_MS = 10000;

/**
 * The wait a 429 asked for, in ms, read from its `Retry-After` header — delay-seconds or an HTTP
 * date (RFC 9110 §10.2.3) — capped at `MAX_RETRY_AFTER_MS`; `null` when the response carries none
 * the client can read.
 *
 * @param {{headers?: {get?: Function}}} response - A fetch Response (or a test's shape of one).
 * @param {number} [now=Date.now()] - The instant an HTTP date is measured from.
 * @returns {number|null}
 */
export function readRetryAfterMs(response, now = Date.now()) {
  const header =
    response && response.headers && typeof response.headers.get === 'function'
      ? response.headers.get('retry-after')
      : null;
  if (header === null || header === undefined || String(header).trim() === '') return null;
  const text = String(header).trim();
  let ms;
  if (/^\d+$/.test(text)) {
    ms = Number(text) * 1000;
  } else {
    const at = Date.parse(text);
    if (Number.isNaN(at)) return null;
    ms = at - now;
  }
  return Math.min(Math.max(ms, 0), MAX_RETRY_AFTER_MS);
}

/**
 * Build an `ERROR` result (the lookup failed). Every call site passes `httpStatus` (number or null),
 * `retryable` and `retryAfterMs` (the capped `Retry-After` of a 429, else null) explicitly — no defaults.
 * @param {Error} error
 * @param {{httpStatus: (number|null), retryable: boolean, retryAfterMs: (number|null)}} meta
 * @returns {{status: string, error: Error, httpStatus: (number|null), retryable: boolean, retryAfterMs: (number|null)}}
 */
function errorResult(error, { httpStatus, retryable, retryAfterMs }) {
  return { status: ResolveStatus.ERROR, error, httpStatus, retryable, retryAfterMs };
}

/**
 * Cache for subdomain→company resolution with TTL
 * @type {Map<string, Object>}
 */
const resolutionCache = new Map();

/**
 * Cache for companyId→company data mapping
 * @type {Map<string, Object>}
 */
const companyDataCache = new Map();

/**
 * Clear the resolution cache
 * @returns {void}
 */
export function clearResolutionCache() {
  resolutionCache.clear();
  companyDataCache.clear();
}

/**
 * Check if a cache entry is still valid
 * @param {Object} entry - Cache entry to check
 * @returns {boolean} True if cache entry is still valid
 */
function isCacheValid(entry) {
  // Callers (resolveCompanyFromSubdomain) check `entry` truthiness before calling.
  const now = Date.now();
  return now - entry.timestamp < CACHE_TTL_MS;
}

/**
 * Parse subdomain from a URL
 *
 * @param {string} [url] - URL to parse (defaults to window.location.href)
 * @returns {string|null} Subdomain or null if none found
 * @throws {Error} If URL format is invalid
 */
export function parseSubdomain(url) {
  try {
    const targetUrl = url || (typeof window !== 'undefined' ? window.location.href : null);

    if (!targetUrl) {
      throw new Error('Invalid URL format');
    }

    // Parsed HERE, so an unparseable URL's own TypeError is the cause chained below
    // (SUBDOMAIN-RESOLVE-001); the scheme decision is the ONE web-address check's.
    const urlObj = new URL(targetUrl);

    if (!isWebAddress(urlObj, { protocols: WEB_PROTOCOLS.HTTP_AND_HTTPS })) {
      throw new Error('Invalid URL format');
    }

    const hostname = urlObj.hostname;

    // The loopback machine itself (the ONE rule, cross-app-policy hostMatching)
    if (isBareLocalHost(hostname)) {
      return 'localhost';
    }

    // Handle raw IP addresses — treat as localhost for dev access
    if (isIPv4Address(hostname)) {
      return 'localhost';
    }

    // Split hostname into parts
    const parts = hostname.split('.');

    // Handle {subdomain}.localhost format for local development
    if (parts.length === 2 && parts[1] === 'localhost') {
      return parts[0];
    }

    // Need at least 3 parts for a subdomain (e.g., subdomain.domain.com)
    if (parts.length < 3) {
      return null;
    }

    return parts[0];
  } catch (parseError) {
    // SUBDOMAIN-RESOLVE-001: surface the real parse failure (was a bare catch)
    // before re-throwing the canonical, callable "Invalid URL format" contract.
    logResolverWarning('parseSubdomain: could not parse URL', { url, error: parseError });
    throw new Error('Invalid URL format', { cause: parseError });
  }
}

/**
 * Development company resolver service
 * Injected via setDevCompanyResolver() during app initialization
 * @type {Object|null}
 */
let devCompanyResolver = null;

/**
 * Set the development company resolver service
 * Call during app initialization to enable localhost/development resolution
 *
 * @param {Object} resolver - Development company resolver
 * @param {Function} resolver.resolve - Function to resolve subdomain to companyId
 * @param {Function} resolver.shouldHandle - Function to check if resolver should handle subdomain
 */
export function setDevCompanyResolver(resolver) {
  devCompanyResolver = resolver;
}

/**
 * Get the current development company resolver
 * @returns {Object|null} Current resolver or null
 */
export function getDevCompanyResolver() {
  return devCompanyResolver;
}

/**
 * Resolve a company from a subdomain via the `/companies/resolve` API.
 *
 * SUBDOMAIN-RESOLVE-001: returns a DISCRIMINATED result (never the historical
 * `string|null`). A definitive absence (`404`/`403`/`400`) is `NOT_FOUND`
 * (terminal); a failed lookup (`5xx` / `408` / `429` / network reject / abort /
 * unparseable-or-companyId-less `200`) is `ERROR` with `retryable:true` so the
 * caller can retry and, if it stays down, show a distinct "temporarily
 * unavailable" state — never the false "not found" page. The real error is
 * logged, not swallowed.
 *
 * @param {string} subdomain - Subdomain to resolve.
 * @param {Object} [options]
 * @param {Function} [options.fetcher] - The HTTP seam. Defaults to the shared
 *   `createFetchSeam`, which is LAZY: it reads `globalThis.fetch` at CALL time and rejects with a
 *   named error when there is none, rather than capturing an absent global at module load.
 *
 *   🔴 RES-CLIENT-001 (Part 3 precedent, `portal-analytics`): an INJECTED seam is not a bespoke
 *   transport. This module deliberately does NOT take `createApiFacade` — it resolves the tenant
 *   BEFORE any session exists, and it needs the raw response to tell six outcomes apart that a
 *   payload-resolving facade would collapse: a retryable 5xx, a definitive 404, an unparseable 200
 *   (the SPA's own nginx answering, never a genuine "tenant absent"), a 200 with no companyId, the
 *   success, and an AbortError timeout. Those distinctions are the whole point of this file.
 * @returns {Promise<{status: string, companyId?: string, companyData?: Object,
 *   error?: Error, httpStatus?: (number|null), retryable?: boolean}>}
 * @throws {ViteApiUrlConfigError} When `VITE_API_URL` is a cross-origin URL (XAL-001).
 */
export async function resolveCompanyFromSubdomain(subdomain, { fetcher } = {}) {
  const httpGet = fetcher || createFetchSeam({ label: 'resolveCompanyFromSubdomain' });
  // XAL-001: fail closed — never issue the cross-origin `/companies/resolve`
  // fetch when VITE_API_URL is misconfigured. SubdomainRouter normally
  // short-circuits to the ConfigErrorPage before this runs; this guards any
  // direct caller.
  if (API_BASE_URL_CONFIG_ERROR) {
    throw API_BASE_URL_CONFIG_ERROR;
  }
  if (!subdomain || (typeof subdomain === 'string' && subdomain.trim() === '')) {
    return notFoundResult();
  }

  const normalizedSubdomain = subdomain.trim().toLowerCase();
  const apiUrl = `${API_BASE_URL}/companies/resolve?subdomain=${encodeURIComponent(normalizedSubdomain)}`;

  try {
    // Check cache first (with TTL validation)
    const cachedEntry = resolutionCache.get(normalizedSubdomain);
    if (cachedEntry && isCacheValid(cachedEntry)) {
      return resolvedResult(cachedEntry.companyId, cachedEntry.companyData);
    }

    // For localhost/development, use injected dev resolver if configured
    if (
      devCompanyResolver &&
      typeof devCompanyResolver.shouldHandle === 'function' &&
      devCompanyResolver.shouldHandle(normalizedSubdomain)
    ) {
      const companyId = await devCompanyResolver.resolve(normalizedSubdomain);
      if (companyId) {
        resolutionCache.set(normalizedSubdomain, {
          companyId,
          timestamp: Date.now(),
        });
        return resolvedResult(companyId);
      }
      return notFoundResult();
    }

    // Call the real API for subdomain resolution, bounded by a timeout so a
    // stalled connection becomes a retryable ERROR (see RESOLVE_FETCH_TIMEOUT_MS)
    // rather than an infinite spinner. The timeout covers connection + headers;
    // once the response is in hand we clear it (a body-parse stall is rare and
    // would still surface as an unparseable-body ERROR below).
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), RESOLVE_FETCH_TIMEOUT_MS);
    let response;
    try {
      response = await httpGet(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const retryable = isRetryableHttpStatus(response.status);
      logResolverWarning(
        retryable
          ? 'resolve returned a retryable HTTP error'
          : 'resolve returned a definitive non-tenant status',
        { subdomain: normalizedSubdomain, url: apiUrl, httpStatus: response.status },
      );
      return retryable
        ? errorResult(new Error(`Resolve failed with HTTP ${response.status}`), {
            httpStatus: response.status,
            retryable: true,
            // A 429 says how long to wait; the hook waits at least that long before its next attempt.
            retryAfterMs: response.status === 429 ? readRetryAfterMs(response) : null,
          })
        : notFoundResult();
    }

    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      // 200 but the body is not JSON → the response came from the SPA's own
      // nginx / a cold proxy, NOT the resolve endpoint. This is a transient
      // wrong-origin/cold-start symptom, never a genuine "tenant absent".
      logResolverWarning('resolve returned an unparseable 200 body', {
        subdomain: normalizedSubdomain,
        url: apiUrl,
        error: parseError,
      });
      return errorResult(parseError, {
        httpStatus: response.status,
        retryable: true,
        retryAfterMs: null,
      });
    }

    if (!data || !data.success || !data.data || !data.data.companyId) {
      // A genuine miss is a 404 (handled above). A 200 without a companyId means
      // the response didn't come from the resolve endpoint — retryable, not a miss.
      logResolverWarning('resolve 200 body missing companyId', {
        subdomain: normalizedSubdomain,
        url: apiUrl,
        httpStatus: response.status,
      });
      return errorResult(new Error('Resolve response missing companyId'), {
        httpStatus: response.status,
        retryable: true,
        retryAfterMs: null,
      });
    }

    const companyId = data.data.companyId;
    const companyData = {
      companyId: data.data.companyId,
      companyName: data.data.companyName,
      subdomain: data.data.subdomain,
      schemaName: data.data.schemaName,
      // TENANT-LOGO: the store's sign-in logo, an absolute media-library address the API judged (unified-models
      // `brandLogoOf`), or null. Absent and null both mean "no logo", so a sign-in shows none.
      logo: typeof data.data.logo === 'string' ? data.data.logo : null,
    };

    // 🔴 Carry `capabilities` THROUGH (R-15, 2026-09-10). This projection named exactly
    // four keys, so even once the API sent them they were dropped one layer later — a
    // capability-less tenant then reached `TenantSiteApp`, whose `caps.has('ecommerce')`
    // gate is false for an empty set, so the customer-account routes never mounted.
    //
    // Copied only when PRESENT, deliberately: `undefined` (this deployment's resolve
    // endpoint has no capability resolver configured) and `[]` (the tenant genuinely has
    // no capabilities) are different facts, and collapsing them to `[]` is what made the
    // original defect invisible. Consumers can tell the two apart, and
    // `assertResolvedCapabilities` below turns the first into a loud one.
    if (Array.isArray(data.data.capabilities)) {
      companyData.capabilities = [...data.data.capabilities];
    }

    resolutionCache.set(normalizedSubdomain, {
      companyId,
      companyData,
      timestamp: Date.now(),
    });

    companyDataCache.set(companyId, companyData);

    return resolvedResult(companyId, companyData);
  } catch (error) {
    // Network reject / abort / timeout — transient. Do NOT collapse into a
    // "not found" (the historical defect: bare catch → null → false 404 page).
    // A timeout abort surfaces as an AbortError; distinguish it in the log so a
    // stalled cold-start is diagnosable separately from a hard network reject.
    const isTimeout = Boolean(error) && error.name === 'AbortError';
    logResolverWarning(
      isTimeout ? 'resolve fetch timed out' : 'resolve fetch threw (network/abort)',
      {
        subdomain: normalizedSubdomain,
        url: apiUrl,
        error,
      },
    );
    return errorResult(error, { httpStatus: null, retryable: true, retryAfterMs: null });
  }
}

/**
 * Load company configuration by ID from the subdomain-resolution cache.
 *
 * SUBDOMAIN-RESOLVE-001 (Gen-0, platform-not-tenant): returns ONLY the real
 * resolved company fields. The former `settings`/`branding`/`features` blocks
 * were **fabricated** tenant data (hardcoded `America/New_York` / `USD` / brand
 * colors / feature flags) attached to every company regardless of tenant — a
 * "build for the platform, not a single tenant" violation, and worse, fake data
 * served to live consumers. They were also effectively unread (pos-web
 * `CompanyContext` re-exposed them with an `|| {}` fallback and nothing consumed
 * them; `@puffin-core/login-shell` read only the always-`null` `branding.logo`),
 * so removing them is safe. Real per-tenant settings/branding/features must come
 * from a real API — never fabricated defaults. The store's sign-in `logo` is that:
 * the resolve endpoint's own answer (TENANT-LOGO, unified-models `brandLogoOf`).
 *
 * @param {string} companyId - Company ID
 * @returns {Promise<Object|null>} Real company data, or null if not cached.
 */
export async function loadCompanyConfiguration(companyId) {
  if (!companyId) {
    return null;
  }

  const cachedData = companyDataCache.get(companyId);
  if (cachedData) {
    return {
      companyId: cachedData.companyId,
      name: cachedData.companyName,
      companyName: cachedData.companyName,
      subdomain: cachedData.subdomain,
      schemaName: cachedData.schemaName,
      logo: cachedData.logo,
    };
  }

  return null;
}

/**
 * Validate subdomain format for company registration.
 *
 * @param {string} subdomain - Subdomain to validate
 * @returns {boolean} True if valid for company use
 */
export function isValidSubdomain(subdomain) {
  if (!subdomain || typeof subdomain !== 'string') {
    return false;
  }

  if (subdomain.length < 3 || subdomain.length > 63) {
    return false;
  }

  const normalizedSubdomain = subdomain.toLowerCase();

  if (RESERVED_SUBDOMAINS.includes(normalizedSubdomain)) {
    return false;
  }

  if (isDevSubdomain(normalizedSubdomain)) {
    return false;
  }

  const validPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
  return validPattern.test(subdomain);
}

/**
 * Get current subdomain from window.location
 * @returns {string|null} Current subdomain
 */
export function getCurrentSubdomain() {
  try {
    return parseSubdomain();
  } catch (error) {
    // SUBDOMAIN-RESOLVE-001: log the swallowed parse failure (was a bare catch);
    // returning null ("no detectable subdomain") remains the correct contract.
    logResolverWarning('getCurrentSubdomain: parseSubdomain threw', { error });
    return null;
  }
}

/**
 * Resolve the company for the current URL's subdomain.
 *
 * SUBDOMAIN-RESOLVE-001: forwards the discriminated result of
 * {@link resolveCompanyFromSubdomain}. An undetectable subdomain resolves to
 * `{ status: 'not-found' }`.
 *
 * @returns {Promise<{status: string, companyId?: string, companyData?: Object,
 *   error?: Error, httpStatus?: (number|null), retryable?: boolean}>}
 */
export async function resolveCompanyId() {
  const subdomain = getCurrentSubdomain();
  return resolveCompanyFromSubdomain(subdomain);
}
